import React, {
  useState,
  useEffect,
  useCallback,
  FunctionComponent,
  Fragment,
} from "react";
import {
  DatePicker,
  Row,
  Col,
  Button,
  Collapse,
  Select,
  Radio,
  Tag,
  Spin,
  Space,
  Card,
  Statistic,
} from "antd";
import PropTypes from "prop-types";
import moment, { Moment } from "moment";
import { connect } from "react-redux";
import {
  AdsAndSmbsRecord,
  AdsOver90DaysRecord,
  YoyAdsRecord,
} from "../../redux/types/reportTypes";
import { StoreType } from "../../redux/store";
import fetchAdsAndSmbs, {
  FetchAdsAndSmbs,
} from "../../redux/actions/reports/fetchAdsAndSmbs";
import fetchYoyAds, { FetchYoyAds } from "../../redux/actions/reports/fetchYoyAds";
import AdsAndSmbsTable from "./AdsAndSmbsTable";
import "./index.scss";
import {
  ClusterOutlined,
  TeamOutlined,
  NumberOutlined,
  PercentageOutlined,
} from "@ant-design/icons";
import fetchTotalAds, { FetchTotalAds } from "../../redux/actions/reports/fetchTotalAds";
import fetchPercentageOfOrigamiSites, {
  FetchPercentageOfOrigamiSites,
} from "../../redux/actions/reports/fetchPercentageOfOrigamiSites";
import fetchAdsOver90Days, {
  FetchAdsOver90Days,
} from "../../redux/actions/reports/fetchAdsOver90Days";
import AdsOver90DaysGraph from "./AdsOver90DaysGraph";
import LoadingGraph from "../../wrappers/LoadingGraph";
import YoyAdsGraph from "./YoyAdsGraph";
import AdsOver90DaysCSV from "./AdsOver90DaysCSV";
import YoyAdsCSV from "./YoyAdsCSV";
import AdsAndSmbsCSV from "./AdsAndSmbsCSV";

type AdsAndSmbsProps = {
  fetchAdsAndSmbs: FetchAdsAndSmbs;
  fetchTotalAds: FetchTotalAds;
  fetchPercentage: FetchPercentageOfOrigamiSites;
  fetchYoyAds: FetchYoyAds;
  fetchAds: FetchAdsOver90Days;
  adsAndSmbsLoading: boolean;
  adsAndSmbsRecords: Array<AdsAndSmbsRecord>;
  adsLoading: boolean;
  adsTotal: number | undefined;
  percentageLoading: boolean;
  percentageTotal: number | undefined;
  adsOver90Loading: boolean;
  adsOver90Records: Array<AdsOver90DaysRecord>;
  yoyLoading: boolean;
  yoyRecords: Array<YoyAdsRecord>;
};

const initialFilters = () => {
  const endDate = moment();
  const startDate = moment().add(-1, "month");
  return { startDate, endDate };
};

export const AdsAndSmbs: FunctionComponent<AdsAndSmbsProps> = ({
  fetchAdsAndSmbs,
  fetchTotalAds,
  fetchPercentage,
  fetchAds,
  fetchYoyAds,
  adsAndSmbsLoading,
  adsAndSmbsRecords,
  adsLoading,
  adsTotal,
  percentageLoading,
  percentageTotal,
  adsOver90Loading,
  adsOver90Records,
  yoyLoading,
  yoyRecords,
}: AdsAndSmbsProps) => {
  const [startDate, setStartDate] = useState<Moment>(moment());
  const [endDate, setEndDate] = useState<Moment>(moment());
  const [clusters, setClusters] = useState<Array<string>>();
  const [partners, setPartners] = useState<Array<number>>();
  const [origami, setOrigami] = useState<string>();

  const setInitialState = useCallback(() => {
    const initial = initialFilters();
    fetchAdsAndSmbs(initial);
    fetchTotalAds(initial);
    fetchPercentage(initial);
    fetchAds({});
    fetchYoyAds({});
    setStartDate(initial.startDate);
    setEndDate(initial.endDate);
    setClusters([]);
    setPartners([]);
    setOrigami("");
  }, [fetchAdsAndSmbs, fetchTotalAds, fetchPercentage, fetchAds, fetchYoyAds]);

  useEffect(() => {
    setInitialState();
  }, [setInitialState]);

  const onCalendarChange = (dates: any) => {
    const [startDate, endDate] = dates as [Moment, Moment];
    setStartDate(startDate);
    setEndDate(endDate);
  };

  const onClick = () => {
    const currentFilters = filters();
    fetchAdsAndSmbs(currentFilters);
    fetchTotalAds(currentFilters);
    fetchPercentage(currentFilters);
    fetchAds({ clusters, partners, origami });
    fetchYoyAds({ clusters, partners, origami });
  };

  const filters = () => ({
    startDate,
    endDate,
    clusters,
    partners,
    origami,
  });

  const uniqueClusters = () => [...new Set(adsAndSmbsRecords.map((r) => r.cluster))];

  const clusterOptions = () =>
    uniqueClusters().map((cluster) => (
      <Select.Option key={cluster} value={cluster}>
        {cluster}
      </Select.Option>
    ));

  const adsAndSmbsExtra = () => {
    if (adsAndSmbsLoading) return <Spin />;
    return (
      <Fragment>
        <Tag color="geekblue">{`${adsAndSmbsRecords.length} Result(s)`}</Tag>
        <AdsAndSmbsCSV data={adsAndSmbsRecords} />
      </Fragment>
    );
  };

  const partnerOptions = () =>
    adsAndSmbsRecords.map((record) => (
      <Select.Option key={record.publisher} value={record.publisher}>
        {record.name}
      </Select.Option>
    ));

  return (
    <Space direction="vertical" className="ads-and-smbs">
      <Row gutter={16}>
        <Col span={6}>
          <Radio.Group
            defaultValue=""
            buttonStyle="solid"
            value={origami}
            onChange={(event) => setOrigami(event.target.value)}
          >
            <Radio.Button value="">Both</Radio.Button>
            <Radio.Button value="0">Legacy</Radio.Button>
            <Radio.Button value="1">Origami</Radio.Button>
          </Radio.Group>
        </Col>
        <Col span={6}>
          <DatePicker.RangePicker
            allowClear={false}
            onCalendarChange={onCalendarChange}
            value={[startDate, endDate]}
          />
        </Col>
        <Col span={2}>
          <Button type="primary" onClick={onClick}>
            Filter
          </Button>
        </Col>
        <Col span={2}>
          <Button onClick={setInitialState}>Clear</Button>
        </Col>
      </Row>
      <Collapse expandIconPosition="right" className="filters-collapse">
        <Collapse.Panel header="Filters" key="1">
          <Row gutter={16} className="filters-container">
            <Col span={12}>
              <Select
                mode="multiple"
                style={{ width: "100%" }}
                placeholder="Select Clusters"
                onChange={(value) => setClusters(value)}
                value={clusters}
              >
                {clusterOptions()}
              </Select>
            </Col>
            <Col span={12}>
              <Select
                mode="multiple"
                style={{ width: "100%" }}
                placeholder="Select Partners"
                onChange={(value) => setPartners(value)}
                value={partners}
              >
                {partnerOptions()}
              </Select>
            </Col>
          </Row>
        </Collapse.Panel>
      </Collapse>
      <Row>
        <Col span={14}>
          <Card title="Ads / SMBs" extra={adsAndSmbsExtra()} className="table-container">
            <AdsAndSmbsTable loading={adsAndSmbsLoading} records={adsAndSmbsRecords} />
          </Card>
        </Col>
        <Col span={10}>
          <Row>
            <Col span={12}>
              <Card>
                <Statistic
                  title="Clusters"
                  value={uniqueClusters().length}
                  prefix={<ClusterOutlined spin={adsAndSmbsLoading} />}
                />
              </Card>
            </Col>
            <Col span={12}>
              <Card>
                <Statistic
                  title="Total Ads"
                  value={adsTotal}
                  prefix={<NumberOutlined spin={adsLoading} />}
                />
              </Card>
            </Col>
            <Col span={12}>
              <Card>
                <Statistic
                  title="Sites"
                  value={adsAndSmbsRecords.length}
                  prefix={<TeamOutlined spin={adsAndSmbsLoading} />}
                />
              </Card>
            </Col>
            <Col span={12}>
              <Card>
                <Statistic
                  title="% Origami Sites"
                  value={percentageTotal || 0}
                  precision={2}
                  prefix={<PercentageOutlined spin={percentageLoading} />}
                />
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>
      <Row>
        <Col span={12}>
          <LoadingGraph
            title="Ads Over Last 90 Days"
            loading={adsOver90Loading}
            records={adsOver90Records}
            Component={AdsOver90DaysGraph}
            Extra={AdsOver90DaysCSV}
          />
        </Col>
        <Col span={12}>
          <LoadingGraph
            title="YOY Ads"
            loading={yoyLoading}
            records={yoyRecords}
            Component={YoyAdsGraph}
            Extra={YoyAdsCSV}
          />
        </Col>
      </Row>
    </Space>
  );
};

const mapDispatchToProps = {
  fetchAdsAndSmbs,
  fetchTotalAds,
  fetchYoyAds,
  fetchPercentage: fetchPercentageOfOrigamiSites,
  fetchAds: fetchAdsOver90Days,
};

const mapStateToProps = ({
  reports: {
    ads_and_smbs: { loading: adsAndSmbsLoading, records: adsAndSmbsRecords },
    total_ads: { loading: adsLoading, records: adsRecords },
    ads_over_90_days: { loading: adsOver90Loading, records: adsOver90Records },
    yoy_ads: { loading: yoyLoading, records: yoyRecords },
    percentage_of_origami_sites: {
      loading: percentageLoading,
      records: percentageRecords,
    },
  },
}: StoreType) => ({
  adsAndSmbsLoading,
  adsAndSmbsRecords,
  adsLoading,
  adsTotal: adsRecords[0]?.count,
  percentageLoading,
  percentageTotal: parseFloat(String(percentageRecords[0]?.count)),
  adsOver90Loading,
  adsOver90Records,
  yoyLoading,
  yoyRecords,
});

AdsAndSmbs.propTypes = {
  fetchAdsAndSmbs: PropTypes.func.isRequired,
  fetchTotalAds: PropTypes.func.isRequired,
  fetchPercentage: PropTypes.func.isRequired,
  fetchAds: PropTypes.func.isRequired,
  adsAndSmbsLoading: PropTypes.bool.isRequired,
  adsAndSmbsRecords: PropTypes.array.isRequired,
  adsLoading: PropTypes.bool.isRequired,
  adsTotal: PropTypes.number,
  percentageLoading: PropTypes.bool.isRequired,
  percentageTotal: PropTypes.number,
  adsOver90Loading: PropTypes.bool.isRequired,
  adsOver90Records: PropTypes.array.isRequired,
  yoyLoading: PropTypes.bool.isRequired,
  yoyRecords: PropTypes.array.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(AdsAndSmbs);
